<script setup lang="ts">
	import { type AccessibleObject } from "@ptx-autonomous/libs/s3";
	import {
		applyChipFilters,
		Icon,
		LoadingSkeletonRows,
		TagSearchBox,
		type FilterChip,
		type TagDef,
	} from "@ptx-autonomous/libs/ui";
	import { ref, watch } from "vue";
	import { computed } from "vue";
	import { useI18n } from "vue-i18n";
	import { fetchAuthSession } from "aws-amplify/auth";
	import { S3Client } from "@aws-sdk/client-s3";
	import { createPresignedDownloadUrl } from "@ptx-autonomous/libs/s3";
	import type { AwsConfiguration } from "../../types/common";
	import { VIDEO_EXTENSIONS } from "../../types/constants";

	const { t } = useI18n();

	const thumbnailUrls = ref<Record<string, string>>({});

	const isVideoFile = (fileKey: string): boolean => {
		const lowerKey = fileKey.toLowerCase();
		return VIDEO_EXTENSIONS.some(ext => lowerKey.endsWith(ext));
	};
	const props = defineProps<{
		files: AccessibleObject[];
		bucketName: string;
		loading: boolean;
		managerFiles: boolean;
		message: string | undefined;
		showTitle: boolean;
		title?: string;
		disableAdd?: boolean;
		awsConfiguration: AwsConfiguration;
	}>();

	async function loadAndCacheThumbnail(
		file: AccessibleObject,
		bucketName: string,
	) {
		const thumbnailKey = file.allTags?.["thumbnail-name"];

		if (!thumbnailKey || thumbnailKey === "none") {
			return null;
		}

		if (thumbnailUrls.value[thumbnailKey]) {
			return thumbnailUrls.value[thumbnailKey];
		}

		try {
			const authSession = await fetchAuthSession();

			if (
				!authSession.credentials?.accessKeyId ||
				!authSession.credentials?.secretAccessKey ||
				!authSession.credentials?.sessionToken
			) {
				return null;
			}

			const s3Client = new S3Client({
				region: props.awsConfiguration.region,
				credentials: {
					accessKeyId: authSession.credentials.accessKeyId,
					secretAccessKey: authSession.credentials.secretAccessKey,
					sessionToken: authSession.credentials.sessionToken,
				},
			});

			const downloadUrl = await createPresignedDownloadUrl(
				s3Client,
				bucketName,
				thumbnailKey,
			);

			thumbnailUrls.value[thumbnailKey] = downloadUrl;
			return downloadUrl;
		} catch (error) {
			console.error("Error loading thumbnail:", error);
			return null;
		}
	}
	const groupFiles = computed(() => {
		const groups: Record<string, AccessibleObject[]> = {};

		for (const file of filteredFiles.value) {
			const category = file.allTags?.["category"] || "others";
			const isThumbnailFile = file.allTags?.["thumbnail-for"];
			if (isThumbnailFile) {
				continue;
			}
			if (!groups[category]) {
				groups[category] = [];
			}
			groups[category].push(file);
		}

		return groups;
	});

	const getThumbnailUrl = (file: AccessibleObject): string | undefined => {
		const thumbnailKey = file.allTags?.["thumbnail-name"];
		return thumbnailKey ? thumbnailUrls.value[thumbnailKey] : undefined;
	};
	watch(
		() => props.files,
		async (newFiles: AccessibleObject[]) => {
			if (newFiles && newFiles.length > 0) {
				const thumbnailPromises = newFiles
					.filter(file => isVideoFile(file.key))
					.map(file => loadAndCacheThumbnail(file, props.bucketName));
				await Promise.all(thumbnailPromises);
			}
		},
		{ immediate: true },
	);
	const emit = defineEmits<{
		openFile: [fileKey: string];
		addFile: [];
		editFile: [file: AccessibleObject];
		deleteFile: [file: AccessibleObject];
	}>();
	const searchQuery = ref("");
	const searchChips = ref<FilterChip[]>([]);
	const sortBy = ref<string>("category");
	const sortOrder = ref<string>("asc");
	const expandedCategories = ref<Set<string>>(new Set([]));

	const searchTags = computed<TagDef[]>(() => [
		{ key: "key", label: t("common.fileName") },
		{ key: "category", label: t("common.category") },
		{ key: "description", label: t("common.description") },
	]);

	// Category and description live under allTags, flatten them so tag search can read them directly.
	const searchableFiles = computed(() =>
		props.files.map(file => ({
			...file,
			category: file.allTags?.["category"] || "others",
			description: file.allTags?.["description"] || "",
		})),
	);

	const filteredFiles = computed(() =>
		applyChipFilters(
			searchableFiles.value,
			searchChips.value,
			searchTags.value,
			searchQuery.value,
		),
	);

	const hasActiveSearch = computed(
		() =>
			searchChips.value.length > 0 || searchQuery.value.trim().length > 0,
	);

	// Auto-expand categories with matches while searching, so results are visible without manual toggling.
	watch([groupFiles, hasActiveSearch], ([groups, active]) => {
		if (active) {
			expandedCategories.value = new Set(Object.keys(groups));
		}
	});

	const visibleCategories = computed(() => {
		let filteredCategories = Object.keys(groupFiles.value);
		if (sortBy.value === "category") {
			filteredCategories.sort((a, b) => {
				if (sortOrder.value === "asc") {
					return a.localeCompare(b);
				} else {
					return b.localeCompare(a);
				}
			});
		}
		return filteredCategories;
	});
	const capitalizedText = (str: string): string =>
		str
			.split(" ")
			.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
			.join(" ");
	const getSortedFilesForCategory = (category: string) => {
		const files = groupFiles.value[category] || [];

		if (sortBy.value === "name") {
			return [...files].sort((a, b) => {
				const comparison = a.key
					.toLowerCase()
					.localeCompare(b.key.toLowerCase());
				return sortOrder.value === "asc" ? comparison : -comparison;
			});
		}

		return files;
	};
	const toggleCategory = (category: string) => {
		if (expandedCategories.value.has(category)) {
			expandedCategories.value.delete(category);
		} else {
			expandedCategories.value.add(category);
		}
	};
	// Sort options
	const sortOptions = computed(() => [
		{ value: "name", label: t("common.fileName") },
		{ value: "category", label: t("common.category") },
	]);

	// Handle sort change
	const handleSortChange = (newSortBy: string) => {
		if (sortBy.value === newSortBy) {
			// Toggle sort order if same field
			sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
		} else {
			// Change sort field and reset to ascending
			sortBy.value = newSortBy;
			sortOrder.value = "asc";
		}
	};
	const dateOptions: Intl.DateTimeFormatOptions = {
		year: "numeric",
		month: "short",
		day: "2-digit",
		timeZone: "UTC",
	};

	const formatDate = (date: string, options: Intl.DateTimeFormatOptions) => {
		return new Date(date).toLocaleDateString("en-US", options);
	};

	const handleEditClick = (event: Event, file: AccessibleObject) => {
		event.stopPropagation();
		emit("editFile", file);
	};

	const handleDeleteClick = (event: Event, file: AccessibleObject) => {
		event.stopPropagation();
		emit("deleteFile", file);
	};
</script>

<template>
	<div
		class="ml-8 box-border flex h-screen flex-col gap-4 overflow-y-auto p-8 pr-16 pb-2"
	>
		<div
			v-if="showTitle"
			class="flex flex-wrap items-center justify-between gap-1"
		>
			<div class="flex items-center gap-1">
				<h1 class="title">{{ title ?? "Files" }}</h1>
			</div>

			<div
				class="flex gap-2"
				v-if="managerFiles"
			>
				<button
					class="button button-primary"
					@click="emit('addFile')"
					:disabled="disableAdd"
				>
					<i class="material-icons">add</i>
					<span>{{ t("common.addDocument") }}</span>
				</button>
			</div>
		</div>

		<!-- Search and Sort Controls -->
		<div
			class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
		>
			<!-- Search Bar -->
			<div class="min-w-[12rem] flex-1">
				<TagSearchBox
					v-model:chips="searchChips"
					v-model:query="searchQuery"
					:tags="searchTags"
					:data="searchableFiles"
					placeholder=""
					class="mt-10 mb-4"
				/>
			</div>

			<!-- Sort Controls -->
			<div class="flex items-center gap-2">
				<span class="text-grey-750 text-sm">
					{{ t("files.sortByLabel") }}
				</span>
				<div class="flex gap-1">
					<button
						v-for="option in sortOptions"
						:key="option.value"
						@click="handleSortChange(option.value)"
						:class="[
							'text-grey-750 inline-flex items-center gap-1 rounded-md px-3 py-1 text-sm transition-colors',
							sortBy === option.value
								? 'bg-green-600 font-bold text-white'
								: 'text-gray-700',
						]"
					>
						{{ option.label }}
						<Icon
							v-if="sortBy === option.value"
							:name="
								sortOrder === 'asc'
									? 'chevron-up'
									: 'chevron-down'
							"
							class="h-3 w-3"
						/>
					</button>
				</div>
			</div>
		</div>

		<div
			class="text-red-500"
			v-if="message"
		>
			{{ message }}
		</div>

		<div class="flex flex-col overflow-y-auto">
			<template v-if="!loading && visibleCategories.length > 0">
				<div
					v-for="categoryName in visibleCategories"
					:key="categoryName"
					class="flex flex-col gap-2"
				>
					<button
						@click="toggleCategory(categoryName)"
						class="hover:bg-black-50 flex w-full items-center justify-between border-b border-gray-300 py-3 transition-colors"
					>
						<span class="font-semibold text-gray-900">{{
							capitalizedText(categoryName)
						}}</span>
						<Icon
							:name="
								expandedCategories.has(categoryName)
									? 'chevron-down'
									: 'chevron-up'
							"
							class="h-5 w-5 text-green-600"
						/>
					</button>

					<div
						v-if="
							expandedCategories.has(categoryName) &&
							getSortedFilesForCategory(categoryName).length > 0
						"
						class="mt-2 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
					>
						<!-- Card only: buttons slide up over the image on hover (admin only) -->
						<div
							v-for="file in getSortedFilesForCategory(
								categoryName,
							)"
							:key="file.key"
							class="group relative flex aspect-[2/1] w-full cursor-pointer flex-col self-stretch overflow-hidden rounded-lg border border-green-400 shadow-sm transition-shadow hover:border-green-950 hover:shadow-lg"
							@click="emit('openFile', file.key)"
						>
							<!-- Video: thumbnail, play overlay, black transparent text bar -->
							<template v-if="isVideoFile(file.key)">
								<div
									class="relative flex h-full min-h-full w-full"
								>
									<img
										v-if="getThumbnailUrl(file)"
										:src="getThumbnailUrl(file)"
										:alt="file.key"
										class="absolute inset-0 h-full w-full object-cover"
									/>
									<div
										v-else
										class="absolute inset-0 flex items-center justify-center bg-green-50"
									>
										<Icon
											name="video"
											class="h-16 w-16 text-gray-500"
										/>
									</div>
									<div
										class="absolute top-1/2 left-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white shadow-md"
									>
										<Icon
											name="play"
											class="block h-6 w-6 shrink-0 translate-x-px translate-y-px text-white"
										/>
									</div>
									<div
										class="black-transparent-bg absolute right-0 bottom-0 left-0 px-4 py-3"
									>
										<p
											class="line-clamp-2 text-base leading-snug font-semibold text-white"
											:title="file.key"
										>
											{{
												file.allTags?.["description"] ??
												file.key
											}}
										</p>
										<p
											class="mt-1 line-clamp-1 text-base leading-snug font-semibold text-white"
										>
											{{
												t("files.versionDate", {
													version:
														file.currentVersionNumber,
													date: formatDate(
														file.lastModified!.toString(),
														dateOptions,
													),
												})
											}}
										</p>
									</div>
								</div>
							</template>

							<!-- PDF: icon + text, light green bg -->
							<template v-else>
								<div
									class="flex h-full w-full items-center gap-4 bg-green-50 p-4"
								>
									<div
										class="flex h-30 w-30 shrink-0 items-center justify-center rounded-lg"
									>
										<Icon
											name="pdf"
											class="h-12 w-12 text-green-600"
										/>
									</div>
									<div class="min-w-0 flex-1">
										<h3
											class="line-clamp-2 text-sm font-medium break-words text-gray-900"
											:title="file.key"
										>
											{{
												file.allTags?.["description"] ??
												file.key
											}}
										</h3>
										<p class="mt-1 text-xs text-gray-600">
											{{
												t("files.versionDate", {
													version:
														file.currentVersionNumber,
													date: formatDate(
														file.lastModified!.toString(),
														dateOptions,
													),
												})
											}}
										</p>
									</div>
								</div>
							</template>

							<!-- When admin: Delete & Edit slide up over the card on hover -->
							<div
								v-if="managerFiles"
								class="absolute right-0 bottom-0 left-0 flex translate-y-full items-center justify-end gap-2 rounded-b-lg bg-green-100 px-3 py-2 shadow-lg transition-transform duration-300 ease-out group-hover:translate-y-0"
							>
								<button
									@click="handleDeleteClick($event, file)"
									class="delete-button flex h-9 w-9 shrink-0 items-center justify-center rounded-[2px] text-white shadow-xs hover:opacity-90"
									:title="t('files.deleteFile')"
									:disabled="disableAdd"
								>
									<Icon
										name="delete"
										class="h-5 w-5"
									/>
								</button>
								<button
									@click="handleEditClick($event, file)"
									class="edit-button flex h-9 w-9 shrink-0 items-center justify-center rounded-[2px] text-white shadow-xs hover:opacity-90"
									:title="t('files.editFile')"
									:disabled="disableAdd"
								>
									<Icon
										name="edit"
										class="h-5 w-5"
									/>
								</button>
							</div>
						</div>
					</div>
				</div>
			</template>

			<div
				v-else-if="!loading && filteredFiles.length === 0"
				class="p-8 text-center text-gray-500"
			>
				<p v-if="hasActiveSearch && searchQuery">
					{{
						t("files.noFilesMatchingQuery", { query: searchQuery })
					}}
				</p>
				<p v-else-if="hasActiveSearch">
					{{ t("files.noFilesMatchingSearch") }}
				</p>
				<p v-else>{{ t("files.noFilesFound") }}</p>
			</div>

			<LoadingSkeletonRows
				class="animate-pulse"
				v-else
			/>
		</div>
	</div>
</template>

<style scoped>
	/* Line clamp utility for file names */
	.line-clamp-2 {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.delete-button {
		background-color: var(--color-red-800);
	}

	.edit-button {
		background-color: var(--color-blue-400);
	}

	.play-triangle {
		border-style: solid;
	}

	.black-transparent-bg {
		background-color: var(--color-backdrop);
	}
</style>
